import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, retry, throwError } from 'rxjs';
import { SessionStorageService } from './storage.service';
import { IAccount, UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class CuttingService {
	constructor(
		private httpClient: HttpClient,
		private userService: UserService,
		private translator: TranslateService,
		private sessionStorageService: SessionStorageService,
	) {}

	private getHeaders(account?: IAccount): HttpHeaders {
		const accountId: string = account ? account.id : this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-account-id': accountId,
		});
	}

	private getEngine() {
		return this.userService.account.defaultEngine;
	}

	getSetups(account?: IAccount): Observable<ISetups> {
		const apiUrl = `${environment.awsEnvUri}/cutting/setups`;
		return this.httpClient
			.get<ISetups>(apiUrl, {
				headers: this.getHeaders(account),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createSetup(setup: ISetup): Observable<IRequestSetup> {
		const body: IRequestSetup = toRequestSetup(setup);
		const apiUrl = `${environment.awsEnvUri}/cutting/setups`;
		return this.httpClient
			.post<IRequestSetup>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateSetup(setup: ISetup): Observable<IResponseSetup> {
		const body: IRequestSetup = toRequestSetup(setup);
		const apiUrl = environment.awsEnvUri + setup.self;
		return this.httpClient
			.put<IRequestSetup>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getRules(): Observable<IRules> {
		const apiUrl = `${environment.awsEnvUri}/cutting/rules`;
		return this.httpClient
			.get<IRules>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createRule(rule: IRule): Observable<IRequestRule> {
		const body: IRequestRule = this.mapRuleToRequestRule(rule);
		const apiUrl = `${environment.awsEnvUri}/cutting/rules`;
		return this.httpClient
			.post<IRequestRule>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateRule(rule: IRule): Observable<IResponseRule> {
		const body: IRequestRule = this.mapRuleToRequestRule(rule);
		const apiUrl = environment.awsEnvUri + rule.self;
		return this.httpClient
			.put<IRequestRule>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getBaskets(account?: IAccount): Observable<IBaskets> {
		const apiUrl = `${environment.awsEnvUri}/cutting/baskets`;
		return this.httpClient
			.get<IBaskets>(apiUrl, {
				headers: this.getHeaders(account),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createBasket(basket: IBasket): Observable<IRequestBasket> {
		const body: IRequestBasket = toRequestBasket(basket);
		const apiUrl = `${environment.awsEnvUri}/cutting/baskets`;
		return this.httpClient
			.post<IRequestBasket>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateBasket(basket: IBasket): Observable<IRequestBasket> {
		const body: IRequestBasket = toRequestBasket(basket);
		const apiUrl = environment.awsEnvUri + basket.self;
		return this.httpClient
			.put<IRequestBasket>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getConfigs(): Observable<IConfigs> {
		const apiUrl = `${environment.awsEnvUri}/cutting/configs`;
		return this.httpClient
			.get<IConfig>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createConfig(config: IConfig): Observable<IRequestConfig> {
		const defaultEngine = this.getEngine();

		const body: IRequestConfig = {
			self: config.self,
			prohibitedColorPairing: config.prohibitedColorPairing,
			allowFrameAndSashInSameBasket: config.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: config.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: config.generateRodsForWaste,
			extraRodsPercentage: config.extraRodsPercentage,
			machines: Array.isArray(config.machines)
				? config.machines.map((machine) => ({
						self: machine.self === null ? `/mdm/apps/one/stores/${defaultEngine}/production-machines/${machine.name}` : machine.self,
						divisionType: machine.divisionType,
					}))
				: [],
		};

		const apiUrl = environment.awsEnvUri + config.self;
		return this.httpClient
			.put<IRequestConfig>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getBodyMachineSelf(machine: IMachine): string {
		// if we delete a machine, the machine name is empty and we return self.
		if (!machine.name) {
			return machine.self;
		}
		// if we edit the machine name or division type, the name field is defined
		// and the maybe changed machine name will replace the current machine name.
		const parts = machine.self.split('/');
		parts[parts.length - 1] = machine.name;
		return parts.join('/');
	}

	getBodyMachines(config: IConfig): IResponseMachine[] {
		if (!Array.isArray(config.machines)) {
			return [];
		}
		const inner: IResponseMachine[] = config.machines.map((machine) => ({
			self: this.getBodyMachineSelf(machine),
			divisionType: machine.divisionType,
		}));
		return inner;
	}

	updateConfig(config: IConfig): Observable<IResponseConfig> {
		const body: IRequestConfig = {
			self: config.self,
			prohibitedColorPairing: config.prohibitedColorPairing,
			allowFrameAndSashInSameBasket: config.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: config.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: config.generateRodsForWaste,
			extraRodsPercentage: config.extraRodsPercentage,
			machines: this.getBodyMachines(config),
		};
		const apiUrl = environment.awsEnvUri + config.self;
		return this.httpClient
			.put<IRequestConfig>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	delete(reference: string): Observable<any> {
		const apiUrl = environment.awsEnvUri + reference;
		return this.httpClient
			.delete(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getLastSegment(urlOrObject: string | any, keyPath?: string[]) {
		let url: string;

		if (typeof urlOrObject === 'string') {
			url = urlOrObject;
		} else if (Array.isArray(keyPath)) {
			url = keyPath.reduce((acc, key) => acc[key], urlOrObject);
		} else {
			throw new TypeError('Invalid arguments');
		}

		const parts = url.split('/');
		return parts[parts.length - 1];
	}

	getTranslation(object, keyPath, baseTranslationKey) {
		const url = keyPath.reduce((acc, key) => acc[key], object);
		const parts = url.split('/');
		const lastPart = parts[parts.length - 1];
		const translationKey = `${baseTranslationKey}.${lastPart}`;
		return this.translator.instant(translationKey);
	}

	mapRuleToRequestRule(rule: IRule): IRequestRule {
		const defaultEngine = this.getEngine();
		const body: IRequestRule = {
			self: rule.self,
			disableOptimization: rule.disableOptimization,
			useCoatingInOptimization: rule.useCoatingInOptimization ?? false,
			name: rule.name,
			// Not `rule.priority &&`: 0 is the highest priority, so only an absent one is omitted.
			...(typeof rule.priority === 'number' && { priority: rule.priority }),
			description: rule.description,
			...(rule.batchType && {
				batchType: {
					self: `/mdm/apps/one/stores/${defaultEngine}/production-batch-types/${rule.batchType}`,
				},
			}),
			material: {
				self: `/mdm/apps/one/stores/${defaultEngine}/component-inventory-items/${rule.material}`,
			},
			...(rule.profile && {
				profile: {
					self: `/mdm/apps/one/stores/${defaultEngine}/component-profiles/${rule.profile}`,
				},
			}),
			machine: {
				self: `/mdm/apps/one/stores/${defaultEngine}/production-machines/${rule.machine}`,
			},
			setup: {
				self: rule.setup,
			},
			...(rule.segregation && {
				segregation: {
					identifier: rule.segregation,
				},
			}),
			...(rule.optimization && {
				// This property is not implemented
				optimization: {
					identifier: rule.optimization,
				},
			}),
			...(rule.logisticsFormula?.trim() && {
				logisticsFormula: rule.logisticsFormula,
			}),
		};

		return body;
	}

	// The response itself is passed on, so callers can read its status and the API's validation errors.
	private handleError(error: HttpErrorResponse) {
		if (error.status !== 403) {
			return throwError(() => error);
		}
		return [];
	}
}

function toRequestSetup(setup: ISetup): IRequestSetup {
	const body: IRequestSetup = {
		self: setup.self,
		name: setup.name,
		description: setup.description,
		dimensions: {
			length: {
				minimum: setup.minLength,
				maximum: setup.maxLength,
			},
		},
		cutting: {
			method: {
				self: setup.cuttingMethod,
			},
			kerf: setup.kerf,
			clearance: setup.clearance,
			rear: setup.rear,
			angle: setup.angle,
			oversizing: setup.oversizing,
		},
		sorting: {
			strategy: {
				self: setup.sortingStrategy,
			},
		},
	};

	return body;
}

function toRequestBasket(basket: IBasket): IRequestBasket {
	const body: IRequestBasket = {
		self: basket.self,
		name: basket.name,
		description: basket.description,
		capacity: {
			maximum: basket.capacity,
		},
	};

	return body;
}

export interface IBaseSetup {
	self: string;
	name: string;
	description: string;
	dimensions: {
		length: {
			minimum: number;
			maximum: number;
		};
	};
	cutting: {
		method: {
			self: string;
		};
		kerf: number;
		clearance: number;
		rear: number;
		angle: number;
		oversizing: boolean;
	};
	sorting: {
		strategy: {
			self: string;
		};
	};
}

export interface ISetup {
	self: string;
	name: string;
	description: string;
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
	minLength: number;
	maxLength: number;
	cuttingMethod: string;
	cuttingMethodTranslation: string;
	kerf: number;
	clearance: number;
	rear: number;
	angle: number;
	oversizing: boolean;
	sortingStrategy: string;
	sortingStrategyTranslation: string;
}

export interface ISetups {
	self: string;
	items: IResponseSetup[];
}

export interface IRequestSetup extends IBaseSetup {
	// No additional properties
}

export interface IResponseSetup extends IBaseSetup, IMeta {
	tags: ITag[];
}

export interface IBaseRule {
	self: string;
	name: string;
	disableOptimization: boolean;
	useCoatingInOptimization: boolean;
	priority?: number | null;
	description: string;
	batchType?: {
		self: string;
	};
	material: {
		self: string;
	};
	profile: {
		self: string;
	};
	machine: {
		self: string;
	};
	setup: {
		self: string;
	};
	segregation: {
		identifier: string;
	};
	optimization: {
		identifier: string;
	};
	logisticsFormula?: string | null;
}

export interface IRule {
	self: string;
	name: string;
	disableOptimization: boolean;
	useCoatingInOptimization: boolean;
	priority: number | null;
	description: string;
	batchType?: string;
	material: string;
	profile: string;
	machine: string;
	setup: string;
	segregation: string;
	optimization: string;
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
	logisticsFormula: string | null;
}

export interface IRules {
	self: string;
	items: IResponseRule[];
}

export interface IRequestRule extends IBaseRule {
	// No additional properties
}

export interface IResponseRule extends IBaseRule, IMeta {
	tags: ITag[];
}

export interface IBaseBasket {
	self: string;
	name: string;
	description: string;
	capacity: {
		maximum: number;
	};
}

export interface IBasket {
	self: string;
	name: string;
	description: string;
	capacity: number;
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
}

export interface IBaskets {
	self: string;
	items: IResponseBasket[];
}

export interface IRequestBasket extends IBaseBasket {
	// No additional properties
}

export interface IResponseBasket extends IBaseBasket, IMeta {
	tags: ITag[];
}

export interface IBaseConfig {
	self: string;
	prohibitedColorPairing: string[][];
	allowFrameAndSashInSameBasket: boolean;
	allowMixedCrossSectionInSameBasket: boolean;
	generateRodsForWaste: boolean;
	extraRodsPercentage: number;
	machines: IResponseMachine[];
}

export interface IConfig {
	self: string;
	prohibitedColorPairing: string[][];
	allowFrameAndSashInSameBasket: boolean;
	allowMixedCrossSectionInSameBasket: boolean;
	generateRodsForWaste: boolean;
	extraRodsPercentage: number;
	machines: IMachine[];
}

export interface IConfigs {
	self: string;
	items: IResponseConfig[];
}

export interface IRequestConfig extends IBaseConfig {
	// No additional properties
}

export interface IResponseConfig extends IBaseConfig, IMeta {
	tags: ITag[];
}

export interface IBaseMachine {
	self: string;
}

export interface IMachine extends IBaseMachine {
	name?: string;
	divisionType: DivisionType;
}

export type DivisionType = 'BetweenEveryOptimizationGroup' | 'BetweenEveryMaterial' | 'BetweenEveryRule' | 'Never';
export type DivisionTypeTranslation = 'masterdata.cutting.divisionType.betweenEveryOptimizationGroup' | 'masterdata.cutting.divisionType.betweenEveryMaterial' | 'masterdata.cutting.divisionType.betweenEveryRule' | 'masterdata.cutting.divisionType.never';
export interface IMachineDetails extends IBaseMachine {
	name: string;
	divisionType: DivisionType;
	divisionTypeTranslation: DivisionTypeTranslation;
}

export interface IResponseMachine extends IBaseMachine {
	divisionType: DivisionType;
}

export interface IColor {
	colors: string;
}

export interface ConfigData {
	self: string;
	configData: IResponseConfig;
	editMachine: IMachineDetails;
	otherMachines: IMachineDetails[];
	editColor: IColor;
	otherColors: IColor[];
}

export interface ITag {
	name: string;
	value: string;
}

export interface IMeta {
	meta: {
		createdAt: number;
		createdBy: {
			self: string;
		};
		updatedAt: number;
		updatedBy: {
			self: string;
		};
		version: number;
	};
}
